import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar, MapPin, Clock, Loader2, ChevronRight, X, User, Mail, Phone, Sparkles } from "lucide-react";
import { format, parseISO, isFuture } from "date-fns";
import { toast } from "sonner";

const BookingItem = ({ b, onCancel }) => {
  const start = parseISO(b.start_date);
  const end = parseISO(b.end_date);
  const upcoming = isFuture(end);
  const cancelled = b.status === "cancelled";

  return (
    <div className="card-premium p-5 flex gap-5" data-testid={`booking-row-${b.id}`}>
      <img src={b.bike_snapshot.image} alt={b.bike_snapshot.name} className="w-32 h-32 object-cover rounded-2xl bg-gray-100" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{b.bike_snapshot.brand}</p>
            <h3 className="font-display font-bold text-lg leading-tight">{b.bike_snapshot.name}</h3>
          </div>
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${
            cancelled ? "bg-red-50 text-red-600" :
            upcoming ? "bg-[#3DDC97]/10 text-[#0d8f5b]" : "bg-gray-100 text-gray-600"
          }`}>
            {cancelled ? "Cancelled" : upcoming ? "Upcoming" : "Completed"}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-600">
          <span className="flex items-center gap-1.5"><Calendar size={12} /><span className="font-mono-num">{format(start, "dd MMM")} → {format(end, "dd MMM yyyy")}</span></span>
          <span className="flex items-center gap-1.5"><MapPin size={12} />{b.pickup_point}, {b.city}</span>
          <span className="flex items-center gap-1.5"><Clock size={12} /><span className="font-mono-num">{b.total_hours}h</span></span>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="font-display font-bold text-sm text-gray-600"><span className="capitalize">{b.plan}</span> plan</p>
          <div className="flex items-center gap-2">
            {upcoming && !cancelled && (
              <button onClick={() => onCancel(b.id)} data-testid={`cancel-booking-${b.id}`} className="text-xs font-semibold text-red-500 hover:bg-red-50 px-3 py-2 rounded-full flex items-center gap-1">
                <X size={12} /> Cancel
              </button>
            )}
            <Link to={`/booking/${b.id}`} data-testid={`view-booking-${b.id}`} className="text-xs font-semibold text-ink hover:text-[#2D7FF9] flex items-center gap-1">
              View <ChevronRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const initialTab = params.get("tab") === "profile" ? "profile" : "bookings";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    api.get("/bookings").then((r) => setBookings(r.data)).finally(() => setLoading(false));
  }, []);

  const cancel = async (id) => {
    try {
      await api.patch(`/bookings/${id}/cancel`);
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status: "cancelled" } : b)));
      toast.success("Booking cancelled");
    } catch (e) { toast.error("Cancel failed"); }
  };

  const upcoming = bookings.filter((b) => isFuture(parseISO(b.end_date)) && b.status !== "cancelled");
  const past = bookings.filter((b) => !isFuture(parseISO(b.end_date)) || b.status === "cancelled");

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />

      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
          <p className="text-[10px] uppercase tracking-widest text-[#2D7FF9] font-bold font-mono-num">— DASHBOARD</p>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl mt-3 tracking-tight">Welcome back, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-gray-600 mt-2">Manage your rides and profile.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-gray-100 rounded-full p-1 h-auto">
            <TabsTrigger value="bookings" data-testid="dashboard-tab-bookings" className="rounded-full data-[state=active]:bg-white px-5">My Bookings</TabsTrigger>
            <TabsTrigger value="profile" data-testid="dashboard-tab-profile" className="rounded-full data-[state=active]:bg-white px-5">Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-8">
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" /></div>
            ) : bookings.length === 0 ? (
              <div className="card-premium p-16 text-center">
                <Sparkles className="mx-auto text-[#3DDC97]" size={32} />
                <p className="font-display font-bold text-2xl mt-4">No bookings yet</p>
                <p className="text-gray-600 mt-2">Your future rides will appear here.</p>
                <Link to="/bikes" data-testid="dashboard-browse-btn" className="btn-primary mt-6 inline-flex">Browse bikes</Link>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-5">
                {upcoming.length > 0 && (
                  <div className="lg:col-span-2">
                    <h3 className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-3">Upcoming · {upcoming.length}</h3>
                    <div className="space-y-4">
                      {upcoming.map((b) => <BookingItem key={b.id} b={b} onCancel={cancel} />)}
                    </div>
                  </div>
                )}
                {past.length > 0 && (
                  <div className="lg:col-span-2 mt-8">
                    <h3 className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-3">Past & Cancelled · {past.length}</h3>
                    <div className="space-y-4">
                      {past.map((b) => <BookingItem key={b.id} b={b} onCancel={cancel} />)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile" className="mt-8 max-w-2xl">
            <div className="card-premium p-8">
              <div className="flex items-center gap-5">
                <div className="w-20 h-20 rounded-3xl bg-brand-gradient flex items-center justify-center text-white font-display font-extrabold text-3xl">
                  {user?.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <h2 className="font-display font-bold text-2xl">{user?.name}</h2>
                  <p className="text-sm text-gray-500 font-mono-num">RIDER · ID {user?.id?.slice(0,8).toUpperCase()}</p>
                </div>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-3"><User size={16} className="text-gray-400" /><span className="text-sm font-medium">Full name</span></div>
                  <span className="text-sm font-mono-num">{user?.name}</span>
                </div>
                {user?.email && (
                  <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="flex items-center gap-3"><Mail size={16} className="text-gray-400" /><span className="text-sm font-medium">Email</span></div>
                    <span className="text-sm font-mono-num">{user.email}</span>
                  </div>
                )}
                {user?.mobile && (
                  <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="flex items-center gap-3"><Phone size={16} className="text-gray-400" /><span className="text-sm font-medium">Mobile</span></div>
                    <span className="text-sm font-mono-num">{user.mobile}</span>
                  </div>
                )}
              </div>

              <div className="mt-8 grid grid-cols-3 gap-4">
                <div className="p-4 rounded-xl border border-gray-100 text-center">
                  <p className="font-display font-extrabold text-2xl">{bookings.length}</p>
                  <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">Total rides</p>
                </div>
                <div className="p-4 rounded-xl border border-gray-100 text-center">
                  <p className="font-display font-extrabold text-2xl">{upcoming.length}</p>
                  <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">Upcoming</p>
                </div>
                <div className="p-4 rounded-xl border border-gray-100 text-center">
                  <p className="font-display font-extrabold text-2xl brand-gradient">★</p>
                  <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">Premium</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;
